import type { HydraCollection } from '../types';

import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Style } from '../constants';
import { RouterLink } from './RouterLink';
import Text from './Text';

type Props = Readonly<{
  hydraCollection: HydraCollection<unknown>;
  pageUrl: (page: number) => string;
  align?: 'left' | 'center' | 'right';
}>;

export default function Pagination({
  hydraCollection,
  pageUrl,
  align = 'center',
}: Props) {
  const hydraView = hydraCollection['hydra:view'];

  const getPage = (path: string): number =>
    parseInt(path.match(/page=(\d+)/)?.[1] ?? '1', 10);
  const currentPage = hydraView ? getPage(hydraView['@id']) : 1;
  const totalPages = hydraView?.['hydra:last']
    ? getPage(hydraView['hydra:last'])
    : 1;
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <html.div
      style={[Style.row, Style.alignCenter, styles.container, styles[align]]}
    >
      {hasPrev ? (
        <RouterLink to={pageUrl(currentPage - 1)}>
          <html.div style={[Style.row, Style.alignCenter, styles.navButton]}>
            <CaretLeftIcon size={12} />
            <Text style={styles.navText}>Previous</Text>
          </html.div>
        </RouterLink>
      ) : (
        <html.div style={[Style.row, Style.alignCenter, styles.navButton]}>
          <CaretLeftIcon size={12} color={color.neutral40} />
          <Text style={styles.navTextDisabled}>Previous</Text>
        </html.div>
      )}

      <html.div style={[Style.row, Style.alignCenter, styles.pages]}>
        {new Array(totalPages).fill(1).map((_, i) => {
          const page = i + 1;
          const isCurrent = page === currentPage;
          return isCurrent ? (
            <html.div
              key={page}
              style={[Style.row, Style.centerBoth, styles.pageNumberCurrent]}
            >
              <Text style={styles.pageTextCurrent}>{page}</Text>
            </html.div>
          ) : (
            <RouterLink key={page} to={pageUrl(page)}>
              <html.div
                style={[Style.row, Style.centerBoth, styles.pageNumber]}
              >
                <Text style={styles.pageText}>{page}</Text>
              </html.div>
            </RouterLink>
          );
        })}
      </html.div>

      {hasNext ? (
        <RouterLink to={pageUrl(currentPage + 1)}>
          <html.div style={[Style.row, Style.alignCenter, styles.navButton]}>
            <Text style={styles.navText}>Next</Text>
            <CaretRightIcon size={12} />
          </html.div>
        </RouterLink>
      ) : (
        <html.div style={[Style.row, Style.alignCenter, styles.navButton]}>
          <Text style={styles.navTextDisabled}>Next</Text>
          <CaretRightIcon size={12} color={color.neutral40} />
        </html.div>
      )}
    </html.div>
  );
}

const styles = css.create({
  container: {
    gap: 24,
  },
  left: {
    justifyContent: 'flex-start',
  },
  center: {
    justifyContent: 'center',
  },
  right: {
    justifyContent: 'flex-end',
  },
  navButton: {
    gap: 4,
    paddingBlock: 10,
  },
  navText: {
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral40,
  },
  navTextDisabled: {
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral40,
  },
  pages: {
    gap: 8,
  },
  pageNumber: {
    width: 26,
    paddingInline: 8,
    borderRadius: 8,
    overflow: 'hidden',
  },
  pageNumberCurrent: {
    width: 26,
    paddingInline: 8,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: color.neutral20,
  },
  pageText: {
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral100,
  },
  pageTextCurrent: {
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral100,
  },
});
